import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { ClipLoader } from "react-spinners";

import Spinner from "../../components/Spinner";
import useAuthFetch from "../../hooks/useAuthFetch";
import {
  createCreditNote,
  userCanCreateCreditNote,
} from "../../utils/creditNote";

export function NotaCreditoPage({ user }) {
  const authFetch = useAuthFetch();
  const { token } = useSelector((store) => store.app.user);
  const [orderIdInput, setOrderIdInput] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canCreate = userCanCreateCreditNote(user);

  const fetchOrder = useCallback(async () => {
    const id = orderIdInput.trim();
    if (!id || !/^\d+$/.test(id)) {
      Swal.fire({
        icon: "warning",
        title: "Número de pedido inválido",
        text: "Ingresá un número de orden válido.",
      });
      return;
    }

    setLoading(true);
    setOrder(null);
    try {
      const res = await authFetch(
        `ventas/${id}?filter={"include":[{"relation":"metodoPagoUtilizado"},{"relation":"estado"}]}`,
        null,
        "GET",
        token
      );
      if (!res.ok) {
        throw new Error("No se encontró el pedido");
      }
      const data = await res.json();
      setOrder(data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Pedido no encontrado",
        text: error.message || "Verificá el número de orden e intentá de nuevo.",
      });
    } finally {
      setLoading(false);
    }
  }, [authFetch, orderIdInput, token]);

  const handleCreateCreditNote = async () => {
    if (!order?.id) {
      return;
    }

    const confirm = await Swal.fire({
      icon: "warning",
      title: "¿Crear nota de crédito?",
      html: `Se anulará la factura electrónica de la orden <strong>#${order.id}</strong>.<br/>La orden <strong>no</strong> se cancelará ni cambiará de estado.`,
      showCancelButton: true,
      confirmButtonText: "Crear nota de crédito",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#76236C",
      cancelButtonColor: "#E7004C",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await createCreditNote(authFetch, order.id, token);
      await Swal.fire({
        icon: "success",
        title: "Nota de crédito generada",
        html: result.numeroConsecutivoNotaCredito
          ? `Consecutivo: <strong>${result.numeroConsecutivoNotaCredito}</strong>`
          : result.message,
      });
      fetchOrder();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreate) {
    return (
      <div className="w-full p-content pb-16">
        <p className="text-lg">No tenés permiso para emitir notas de crédito.</p>
      </div>
    );
  }

  return (
    <div className="w-full p-content pb-16 min-h-[400px]">
      {(loading || submitting) && <Spinner />}
      <div className="flex w-full items-center mb-4">
        <Link to="/cuenta">
          <button
            type="button"
            className="bg-pink text-white py-2 px-8 font-bold rounded-full float-right flex justify-center items-center"
          >
            <IoMdArrowRoundBack className="text-lg" /> Volver
          </button>
        </Link>
      </div>

      <p className="text-2xl font-bold mb-2">Notas de crédito</p>
      <p className="text-sm text-gray-600 mb-6">
        Anulá la factura electrónica de un pedido y generá la nota de crédito en
        Lucida sin cancelar la orden.
      </p>

      <div className="flex flex-wrap gap-3 mb-8">
        <input
          type="text"
          inputMode="numeric"
          placeholder="Número de pedido"
          className="flex-1 min-w-[200px] px-4 rounded-full ring-1 ring-borderGray h-12 outline-none focus:ring-purple"
          value={orderIdInput}
          onChange={(e) => setOrderIdInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              fetchOrder();
            }
          }}
        />
        <button
          type="button"
          onClick={fetchOrder}
          disabled={loading}
          className="bg-purple text-white py-2 px-8 rounded-full font-medium disabled:opacity-50"
        >
          Buscar pedido
        </button>
      </div>

      {order && (
        <div className="w-full ring-1 ring-purple p-6 rounded-lg">
          <p className="text-lg font-bold mb-4">Orden #{order.id}</p>
          <p className="mb-2">
            Estado:{" "}
            <span className="font-medium">{order.estado?.valor || "—"}</span>
          </p>
          <p className="mb-2">
            Total:{" "}
            <span className="font-medium">
              {Intl.NumberFormat("es-CR", {
                style: "currency",
                currency: "CRC",
              }).format(order.totalConTransporte)}
            </span>
          </p>
          {order.nombreFactura ? (
            <>
              <p className="mb-2">
                Facturación:{" "}
                <span className="font-medium">{order.nombreFactura}</span>
              </p>
              {order.identificacionFactura && (
                <p className="mb-2">
                  Identificación:{" "}
                  <span className="font-medium">
                    {order.identificacionFactura}
                  </span>
                </p>
              )}
            </>
          ) : (
            <p className="mb-4 text-pink">
              Este pedido no tiene datos de facturación electrónica.
            </p>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            <button
              type="button"
              onClick={handleCreateCreditNote}
              disabled={submitting || !order.nombreFactura}
              className="bg-orange text-white py-2 px-6 rounded-full disabled:opacity-50 flex items-center gap-2"
            >
              Crear nota de crédito
              {submitting && <ClipLoader color="#fff" size={16} />}
            </button>
            <Link to={`/pedidos/${order.id}?desde=/notas-credito`}>
              <button
                type="button"
                className="bg-purple text-white py-2 px-6 rounded-full"
              >
                Ver detalle del pedido
              </button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotaCreditoPage;
